/**
 * Feb 2026 R293zb · CreateSalesOrderDialog — pick lines + partial
 * quantities from an accepted (or superseded) quotation and spawn a
 * fresh Sales Order. Shows the live fulfilment picture so users can't
 * over-order; disables lines that are already fully covered.
 *
 * Backend contracts:
 *   GET  /gst/workspaces/{ws}/estimates/{eid}/fulfillment
 *   POST /gst/workspaces/{ws}/estimates/{eid}/sales-orders
 *     body: { picks: [{estimate_line_id, qty, rate?}], subject? }
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtMoney } from "@/lib/currency";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { CheckCircle2, Package, Loader2, ArrowRight } from "lucide-react";

export default function CreateSalesOrderDialog({ open, onClose, ws, estimate, onCreated }) {
  const [fulfilment, setFulfilment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picks, setPicks] = useState({}); // {line_id: qty}
  const [subject, setSubject] = useState("");

  useEffect(() => {
    if (!open || !estimate?.id) return;
    let cancelled = false;
    setLoading(true);
    setPicks({});
    setSubject(estimate?.subject || "");
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/estimates/${estimate.id}/fulfillment`);
        if (!cancelled) {
          setFulfilment(r.data);
          // Pre-fill with remaining qty so a one-click "fulfil all remaining" works.
          const seed = {};
          (r.data.lines || []).forEach((l) => {
            seed[l.id] = l.qty_remaining > 0 ? l.qty_remaining : 0;
          });
          setPicks(seed);
        }
      } catch (e) {
        if (!cancelled) toast.error(apiErrorMessage(e));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, ws, estimate]);

  const total = useMemo(() => {
    if (!fulfilment) return 0;
    return (fulfilment.lines || []).reduce((s, l) => {
      const q = Number(picks[l.id] || 0);
      return s + q * (l.rate || 0);
    }, 0);
  }, [picks, fulfilment]);

  const anyPicked = useMemo(() =>
    Object.values(picks).some((v) => Number(v) > 0), [picks]);

  const submit = async () => {
    const chosen = (fulfilment?.lines || [])
      .filter((l) => Number(picks[l.id] || 0) > 0)
      .map((l) => ({ estimate_line_id: l.id, qty: Number(picks[l.id]) }));
    if (!chosen.length) {
      toast.error("Pick at least one line with qty > 0");
      return;
    }
    try {
      setSaving(true);
      const r = await api.post(
        `/gst/workspaces/${ws}/estimates/${estimate.id}/sales-orders`,
        { picks: chosen, subject: subject || null });
      toast.success(`Created ${r.data.doc_no}`);
      onCreated?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setSaving(false); }
  };

  const canOrder = fulfilment?.estimate_status === "accepted" || fulfilment?.estimate_status === "superseded";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl w-screen sm:w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="create-so-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            Sales Order from {estimate?.doc_no}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick line items (partial quantities OK). You can run this
            again later to spawn additional SOs until every line is fully ordered.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3 bg-muted/20">
          {loading && (
            <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading fulfilment…
            </div>
          )}
          {!loading && fulfilment && !canOrder && (
            <div className="border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 rounded p-3 text-xs text-amber-800 dark:text-amber-200">
              Quotation must be <b>accepted</b> to spawn Sales Orders.
              Current status: <b>{fulfilment.estimate_status}</b>.
            </div>
          )}
          {!loading && fulfilment && canOrder && (
            <>
              {fulfilment.child_sales_orders?.length > 0 && (
                <div className="border border-border rounded p-2 bg-background">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    Already spawned ({fulfilment.child_sales_orders.length})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {fulfilment.child_sales_orders.map((so) => (
                      <span key={so.id}
                              className="text-[11px] px-2 py-0.5 border border-border rounded bg-muted/40"
                              data-testid={`create-so-child-${so.id}`}>
                        {so.doc_no} · {so.status}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Subject (optional)</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={estimate?.subject || "What's this order for?"}
                  className="rounded-none h-10"
                  data-testid="create-so-subject"
                />
              </div>

              <div className="space-y-2">
                {(fulfilment.lines || []).map((l) => {
                  const q = Number(picks[l.id] || 0);
                  const over = q > l.qty_remaining + 1e-6;
                  const amount = q * (l.rate || 0);
                  return (
                    <div
                      key={l.id}
                      className={`border rounded-lg p-2.5 ${
                        l.fulfilled ? "bg-muted/30 border-dashed border-border opacity-60" :
                        over ? "border-rose-400 bg-rose-50 dark:bg-rose-950/40" : "border-border bg-background"
                      }`}
                      data-testid={`create-so-line-${l.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{l.description || "—"}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            Qty on quotation: <b>{l.qty}</b> · ordered: {l.qty_ordered} ·
                            remaining: <b className={l.qty_remaining > 0 ? "text-emerald-700 dark:text-emerald-300" : ""}>{l.qty_remaining}</b> · rate {fmtMoney(l.rate)}
                          </div>
                        </div>
                        {l.fulfilled ? (
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-800 rounded shrink-0">
                            <CheckCircle2 className="h-3 w-3 inline mr-0.5" /> Done
                          </div>
                        ) : (
                          <div className="shrink-0 w-24">
                            <Label className="text-[10px] text-muted-foreground">Qty</Label>
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              max={l.qty_remaining}
                              step="1"
                              value={picks[l.id] ?? ""}
                              onChange={(e) => setPicks((p) => ({ ...p, [l.id]: e.target.value }))}
                              className={`h-10 text-sm text-right rounded-none ${over ? "border-rose-400" : ""}`}
                              data-testid={`create-so-qty-${l.id}`}
                            />
                          </div>
                        )}
                      </div>
                      {!l.fulfilled && q > 0 && (
                        <div className="text-[11px] text-muted-foreground mt-1 text-right">
                          Line total ≈ <b>{fmtMoney(amount)}</b>
                        </div>
                      )}
                      {over && (
                        <div className="text-[11px] text-rose-700 dark:text-rose-300 mt-1">
                          Can't order more than {l.qty_remaining}. The server will reject this.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-4 py-3 border-t border-border shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex-1 text-sm">
            {anyPicked && (
              <>
                <span className="text-muted-foreground">Order subtotal</span>{" "}
                <b data-testid="create-so-subtotal">{fmtMoney(total)}</b>
                <span className="text-[11px] text-muted-foreground ml-1">(before GST)</span>
              </>
            )}
          </div>
          <Button variant="ghost" onClick={onClose} data-testid="create-so-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canOrder || !anyPicked || saving}
            data-testid="create-so-submit"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating…</> :
              <>Create Sales Order <ArrowRight className="h-4 w-4 ml-1" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
